import { useState, useRef, useEffect } from 'react';
import { AuthLayout } from '../layout/AuthLayout.jsx';
import { FormField } from '../components/FormField.jsx';
import { TextInput } from '../components/TextInput.jsx';
import { PasswordInput } from '../components/PasswordInput.jsx';
import { SubmitButton } from '../components/SubmitButton.jsx';
import { FormError } from '../components/FormError.jsx';
import { api, ApiError } from '../api/client.js';
import { useAuth } from '../state/AuthContext.jsx';
import { formatDateDDMonYYYY } from '../../utils/dates.js';

export function LoginPage({ navigate }) {
  const { setAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [accountStatus] = useState(() => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    const account = params.get('account');
    if (!account) return null;
    return {
      account,
      grace: params.get('grace') === 'true',
      until: params.get('until') || null,
    };
  });
  const emailRef = useRef(null);

  useEffect(() => { emailRef.current?.focus(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const data = await api.login({ email: email.trim(), password });
      setAuthenticated(data.user, data.organization);
      const redirect = popPostLoginRedirect();
      navigate(redirect || '/dashboard');
    } catch (e2) {
      if (e2 instanceof ApiError && e2.code === 'email_not_verified') {
        navigate('/verify-email', `email=${encodeURIComponent(email.trim())}`);
        return;
      }
      setError(e2);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout title="Log in to CritWise" subtitle="Welcome back.">
      {accountStatus?.account === 'deleted' && (
        <p className="login-banner" role="status">
          {accountStatus.grace
            ? `Your account has been deleted. Your organisation's data is preserved until ${formatGraceUntil(accountStatus.until)}.`
            : 'Your account has been deleted.'}
        </p>
      )}
      <form onSubmit={handleSubmit} noValidate>
        <FormError error={error} />
        <FormField label="Email" htmlFor="login-email">
          <TextInput
            id="login-email"
            name="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            ref={emailRef}
            required
          />
        </FormField>
        <FormField label="Password" htmlFor="login-password">
          <PasswordInput
            id="login-password"
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </FormField>
        <SubmitButton loading={submitting}>Log in</SubmitButton>
      </form>
      <div className="auth-links">
        <a
          href="/forgot-password"
          onClick={(ev) => { ev.preventDefault(); navigate('/forgot-password'); }}
        >Forgot password?</a>
        <span className="auth-links-sep">·</span>
        <a
          href="/signup"
          onClick={(ev) => { ev.preventDefault(); navigate('/signup'); }}
        >Create an account</a>
      </div>
    </AuthLayout>
  );
}

function popPostLoginRedirect() {
  try {
    const v = sessionStorage.getItem('postLoginRedirect');
    if (v) sessionStorage.removeItem('postLoginRedirect');
    return v;
  } catch {
    return null;
  }
}

function formatGraceUntil(iso) {
  if (!iso) return 'the end of the grace period';
  // A1 (D-ListDateFormat): the one shared DD Mon YYYY formatter.
  const s = formatDateDDMonYYYY(iso);
  return s === '—' ? 'the end of the grace period' : s;
}
